
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

type Submission = {
  id: string;
  email: string;
  role: string;
  resume_url: string;
  status: string;
  created_at: string;
};

const AgentSubmissions = () => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchSubmissions = async () => {
    const { data, error } = await supabase
      .from('quick_submissions')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as Submission[];
  };

  const { data: submissions, isLoading, error } = useQuery({
    queryKey: ['submissions'],
    queryFn: fetchSubmissions,
  });

  const getResumeUrl = async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('resumes')
        .createSignedUrl(path, 120); // URL valid for 120 seconds (increased from 60)
      
      if (error) throw error;
      
      return data.signedUrl;
    } catch (error: any) {
      toast.error('Failed to get resume download link');
      console.error('Error getting download URL:', error);
      return null;
    }
  };

  const handleDownload = async (submissionId: string, path: string) => {
    setDownloadingId(submissionId);
    
    try {
      // Retry strategy for download
      let attempts = 0;
      let url = null;
      
      while (!url && attempts < 3) {
        attempts++;
        url = await getResumeUrl(path);
        
        if (!url && attempts < 3) {
          // Wait before retry (0.5s, 1s, 2s)
          await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempts - 1)));
        }
      }
      
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Unable to download resume after multiple attempts');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download resume');
    } finally {
      setDownloadingId(null);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'software_engineer':
        return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
      case 'data_scientist':
        return 'bg-purple-500/20 text-purple-500 border-purple-500/30';
      case 'product_manager':
        return 'bg-green-500/20 text-green-500 border-green-500/30';
      default:
        return 'bg-mint/20 text-mint border-mint/30';
    }
  };

  const formatRole = (role: string) => {
    return role.replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  if (isLoading) {
    return <div className="p-6 text-center text-white">Loading submissions...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-white">Error loading submissions</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-6">Agent Submissions</h2>
      
      {submissions && submissions.length === 0 ? (
        <Card className="bg-forest-light border-mint/20">
          <CardContent className="p-6">
            <p className="text-white text-center">No submissions yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {submissions?.map((submission) => (
            <Card key={submission.id} className="bg-forest-light border-mint/20">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white text-xl">{submission.email}</CardTitle>
                    <CardDescription className="text-white/70">
                      Submitted {new Date(submission.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <Badge className={`${getRoleBadgeColor(submission.role)} font-normal`}>
                    {formatRole(submission.role)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center mt-2">
                  <FileText className="h-4 w-4 text-mint mr-2" />
                  <span className="text-white/80 text-sm mr-4">
                    Resume
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-mint hover:text-white hover:bg-mint/20"
                    onClick={() => handleDownload(submission.id, submission.resume_url)}
                    disabled={downloadingId === submission.id}
                  >
                    {downloadingId === submission.id ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-mint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </span>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-1" /> View PDF
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentSubmissions;
